// @ts-nocheck

import cv, { Mat } from 'opencv-ts'
import {prettierLog} from "../../../utils";
import {getBrowserCapabilities} from "../../../utils/browserUtil";
import {downloadModel, ensureModel, getModel, modelExists} from "../../../utils/model";
import {ModelTypeConstant, wasmPaths} from "../../../constants";
import {float32ArrayToBase64, resizeCanvasImage} from "../../../utils/filesUtil";
import * as ort from 'onnxruntime-web/all'
import {debounce} from "../../../utils/debounceThrottle";

let model: any = null;

let modelBasicInfo = getModel(ModelTypeConstant.REMOVE_BG);

let targetWidth = modelBasicInfo.targetWidth;
let targetHeight = modelBasicInfo.targetHeight;
let inputName = modelBasicInfo.inputName;
let defaultExecutionProviders = modelBasicInfo.defaultExecutionProviders;
let loadingNotification = () => {};
const configEnv = (capabilities: {
    webGPU: boolean;
    wasm?: boolean;
    simd: any;
    threads: any;
}) => {
    // ort.env.wasm.wasmPaths = {
    //     'ort-wasm.wasm': `${wasmPaths.url}ort-wasm.wasm`,
    //     'ort-wasm-threaded.wasm': `${wasmPaths.url}ort-wasm-threaded.wasm`,
    //     'ort-wasm-simd.wasm': `${wasmPaths.url}ort-wasm-simd.wasm`,
    //     'ort-wasm-simd-threaded.wasm': `${wasmPaths.url}ort-wasm-simd-threaded.wasm`,
    // }
    // ort.env.wasm.wasmPaths = wasmPaths.url; // 不导入onnxruntime-web库时需要自定义wasmPaths路径
    if (capabilities.webGPU) {
        ort.env.wasm.numThreads = 1;
    } else {
        if (capabilities.threads) {
            ort.env.wasm.numThreads = navigator.hardwareConcurrency ?? 4;// 根据 CPU 核心数动态分配线程
        }
        if (capabilities.simd) {
            ort.env.wasm.simd = true;// 启用 SIMD 指令加速
        }
        ort.env.wasm.proxy = true;// 启用代理模式优化资源加载
    }
    // console.log('env', ort.env.wasm);
}

export const createInferenceSession = async (showAlert: any) => {
    prettierLog({
        msg: 'create InferenceSession',
        type: 'info'
    });
    if(!model) {
        const capabilities = await getBrowserCapabilities();
        configEnv(capabilities);

        const modelBuffer = await downloadModel(ModelTypeConstant.REMOVE_BG, downloadProgressHandle, showAlert);
        model = await ort.InferenceSession.create(modelBuffer, {
            // executionProviders: ['wasm'],
            executionProviders: [capabilities.webGPU ? defaultExecutionProviders : 'wasm'],
            sessionOptions: {
                enableMemPattern: false,  // 禁用内存优化模式
                enableCpuMemArena: true,   // 启用CPU内存池
                memoryLimit: 10334984,
            }
        });
    }
    prettierLog({
        msg: 'InferenceSession created',
        type: 'success'
    });
}
export const recreateInferenceSession = async (showAlert: any, showLoadingNotification: any) => {
    if (model && model.handler && model.handler.dispose) {
        model.handler.dispose();
        prettierLog({
            msg: "model dispose",
            type: "success"
        });
    }
    if (model && model.ioBinding) {
        model.ioBinding.dispose(); // 释放绑定的 GPU 缓冲区
        prettierLog({
            msg: "GPU Buffer dispose",
            type: "success"
        });
    }
    model = null;
    modelBasicInfo = getModel(ModelTypeConstant.REMOVE_BG);
    targetWidth = modelBasicInfo.targetWidth;
    targetHeight = modelBasicInfo.targetHeight;
    inputName = modelBasicInfo.inputName;
    defaultExecutionProviders = modelBasicInfo.defaultExecutionProviders
    loadingNotification = showLoadingNotification;

    await createInferenceSession(showAlert);
}

const downloadProgressHandle = (progressNumber: number) => {
    // console.log("当前下载进度" + progressNumber);
    loadingNotification(progressNumber.toFixed(0), "Model Loading...", "Model Loaded");
}

const preProcess = (image: HTMLImageElement) => {
    prettierLog({
        msg: 'start preprocess',
        type: 'info'
    });

    // 创建 canvas 元素
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d') || new CanvasRenderingContext2D();

    // 调整图像尺寸为模型输入大小
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // 绘制图像到 canvas
    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

    // 获取图像像素数据
    const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
    const pixels = imageData.data; // Uint8ClampedArray 格式的像素数据

    // 将像素数据转换为模型输入格式（float32[1,3,320,320]）
    const inputTensor = new Float32Array(1 * 3 * targetWidth * targetHeight);
    for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i] / 255.0;     // 归一化到 [0,1]
        const g = pixels[i + 1] / 255.0;
        const b = pixels[i + 2] / 255.0;

        // 转换为 CHW 格式（通道优先）
        inputTensor[i / 4] = r;         // 红色通道
        inputTensor[i / 4 + targetWidth * targetHeight] = g; // 绿色通道
        inputTensor[i / 4 + 2 * targetWidth * targetHeight] = b; // 蓝色通道
    }

    prettierLog({
        msg: 'preprocess end',
        type: 'success'
    });

    return { inputName, inputTensor };
}

const runModel = async (inputInfo: any) => {
    prettierLog({
        msg: 'start run',
        type: 'info'
    });
    const { inputName, inputTensor } = inputInfo;

    const input = new ort.Tensor('float32', inputTensor, [1, 3, targetWidth, targetHeight]);
    const results = await model.run({[inputName]: input});

    prettierLog({
        msg: 'run end',
        type: 'success'
    });

    // console.log(results);
    // console.log(model.outputNames);
    return results;
}

const postProcess = async (results: any, image: HTMLImageElement) => {
    prettierLog({
        msg: 'start postProcess',
        type: 'info'
    });
    const outputData = results[model.outputNames[0]].data;
    // 1. 创建 Canvas 并绘制图像
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    let imageData = ctx.createImageData(targetWidth, targetHeight);
    // 将单通道 Float32Array 映射到 RGBA 格式
    for (let i = 0, j = 0; i < outputData.length; i++, j += 4) {
        const value = Math.round(outputData[i] * 255); // 映射到 [0, 255]
        imageData.data[j] = value;     // R
        imageData.data[j + 1] = value; // G
        imageData.data[j + 2] = value; // B
        imageData.data[j + 3] = 255;  // A（不透明）
    }
    ctx.putImageData(imageData, 0, 0);
    // const greyImageBase64 = canvas.toDataURL('image/png');// 模型输出的图像

    // 2、resize模型输出图像
    const resizedCanvas = await resizeCanvasImage(canvas, image.width, image.height);
    // console.log(resizedCanvas.toDataURL('image/png'));// 模型输出的图像调整尺寸后
    const resizeCanvasCtx = resizedCanvas.getContext('2d');
    imageData = resizeCanvasCtx.getImageData(0, 0, image.width, image.height);

    // 3. 加载原始图像
    const originalCanvas = document.createElement('canvas');
    originalCanvas.width = image.width;
    originalCanvas.height = image.height;
    const originalCtx = originalCanvas.getContext('2d');
    originalCtx.drawImage(image, 0, 0, image.width, image.height);
    const originalImageData = originalCtx.getImageData(0, 0, image.width, image.height);

    // 4、裁剪原图像
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = image.width;
    finalCanvas.height = image.height;
    const finalCtx = finalCanvas.getContext('2d');
    const oImageData = originalImageData;// 暂存原始图像数据
    for (let i = 0; i < imageData.data.length; i += 4) {
        const r = oImageData.data[i];
        const g = oImageData.data[i + 1];
        const b = oImageData.data[i + 2];

        // 判断是否为透明
        const isOpacity = imageData.data[i] <= 190 && imageData.data[i + 1] <= 190 && imageData.data[i + 2] <= 190;

        // 设置目标像素（保留原色，仅修改透明度）
        oImageData.data[i] = r; // R
        oImageData.data[i + 1] = g; // G
        oImageData.data[i + 2] = b; // B
        oImageData.data[i + 3] = isOpacity ? 0 : 255; // A
    }
    finalCtx.putImageData(oImageData, 0, 0);

    // 5. 获取处理结果后图像的路径
    const resultImgBase64 = finalCanvas.toDataURL('image/png');
    const originImgBase64 = originalCanvas.toDataURL('image/png');
    // console.log('处理结果后图像的路径:', resultImgBase64);

    prettierLog({
        msg: 'postProcess end',
        type: 'success'
    });
    return { resultImgBase64, originImgBase64, finalCanvas, originalCanvas };
}

export const modelIsLoaded = () => {
    return model !== null;
}
export const imageBgRemove = async (image: HTMLImageElement, showAlert: any) => {
    return new Promise(async (resolve, reject) => {
        // console.log(image);
        if (!model) await createInferenceSession(showAlert);
        const inputInfo = preProcess(image);
        const results = await runModel(inputInfo);

        const res = postProcess(results, image);
        resolve(res);
    })
}
